import WorkflowInstance from '../WorkflowInstance';
import User from '../User';
import { WorkflowState } from '../enums/WorkflowState';
import { Environment } from '../enums/Environment';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../enums/UserRole';
import { PortOpeningRequestsDirection } from '../enums/POR/PortOpeningRequestsDirection';
import AWSPortOpeningRule from './AWSPortOpeningRule';
import CartaId from './CartaId';
import { AWSPortOpeningRequestsTypes } from '../enums/POR/AWSPortOpeningRequestType';
import CartaIdComponent from '../viewModels/CartaId/CartaIdComponent';
import ApprovalContact, { fromUser, fromEmails, fromAppEndorser } from '../interfaces/ApprovalContact';
import { ApprovalContactLabels } from '../ApprovalContactLabels';

export default class AWSPortOpeningRequest {

	id!: number;

	awsPortOpeningRules!: AWSPortOpeningRule[];

	workflowInstance!: WorkflowInstance;

	direction!: PortOpeningRequestsDirection;

	type!: AWSPortOpeningRequestsTypes;

	environment!: Environment;

	contactUser!: User;

	isEmergency!: boolean;

	submitDate!: Date;

	businessJustification!: string;

	comments!: string;

	approvingUser!: User;

	secondaryApprovingUser!: User;

	delegatedApprovingUser!: User;

	hasDelegationApprover!: boolean;

	accountId!: number;

	secondaryAccountId!: number;
	
	accountName: string;

	secondaryAccountName: string;

	cartaId!: CartaId[];

	appEndorser!: CartaIdComponent;

	customerCoordination!: boolean;

	custodianApproval!: boolean;

	accountOwnerId: string;

	secondaryAccountOwnerId: string;

	escalated!: boolean;

	currentApproverDistributionList: string[];

	currentApproverDisplayName: string;

	constructor(obj: any) {
		Object.assign(this, obj);
		this.workflowInstance = new WorkflowInstance(obj.workflowInstance);
		this.direction = new PortOpeningRequestsDirection().fromValue(obj.direction?.value);
		this.awsPortOpeningRules = obj.awsPortOpeningRules?.map(rules =>  new AWSPortOpeningRule(rules));
		const customFields = new CartaIdComponent(obj.appEndorser).fromUser(obj.appEndorser);
		this.appEndorser = new CartaIdComponent({
			...obj.appEndorser,
			customFields: customFields,
		});
	}

	implementationTicket(): string {
		const implementationState = this.workflowInstance.workflowInstanceStates
			.reverse()
			.find(state => state.ticketId !== null);
		return implementationState?.ticketId || '';
	}

	isCancellable(): boolean {
		return (
			authenticatedUser.userId === this.workflowInstance.createdBy.id &&
			[
				WorkflowState.Draft.value,
				WorkflowState.PendingSubscriptionOwnerApproval.value,
				WorkflowState.PendingForDOAGApproval.value,
				WorkflowState.PendingForPolicyTeamApproval.value,
				WorkflowState.Review.value,
			].includes(this.workflowInstance.currentWorkflowInstanceState.workflowState.value)
		);
	}

	currentApprovers(): ApprovalContact[] {
		let approvers: ApprovalContact[] = [];
		const distributionList = fromEmails(this.currentApproverDistributionList);

		switch (this.workflowInstance.currentWorkflowInstanceState.workflowState?.value) {
			case WorkflowState.PendingAccountOwnerApproval.value:
				approvers = [
					{ name: this.currentApproverDisplayName || ApprovalContactLabels.AccountOwner, email: distributionList },
				];
				break;
			case WorkflowState.PendingSecondaryAccountOwnerApproval.value:
				approvers = [
					{
						name: this.currentApproverDisplayName || ApprovalContactLabels.SecondaryAccountOwner,
						email: distributionList,
					},
				];
				break;
			case WorkflowState.PendingNetworkPolicyApproval.value:
				approvers = [{ name: ApprovalContactLabels.NetworkPolicyTeam, email: distributionList }];
				break;
			case WorkflowState.PendingSolutionArchitectureApproval.value:
				approvers = [{ name: ApprovalContactLabels.NetworkPolicySolutionArchitects, email: distributionList }];
				break;
			case WorkflowState.PendingForCyberApproval.value:
				approvers = [{ name: ApprovalContactLabels.CyberSecurityTeam, email: distributionList }];
				break;
			case WorkflowState.PendingForDOAGApproval.value:
				if (this.approvingUser) {
					approvers = [fromUser(this.approvingUser)];
				}
				break;
			case WorkflowState.PendingApplicationApproval.value:
				approvers = [fromAppEndorser(this.appEndorser)];
				break;
		}

		return approvers.filter((approver) => !!approver?.name);
	}

	canViewDOAGApproval(): boolean {
		return authenticatedUser.userId === this.approvingUser?.id;
	}

	canHaveDOAGApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			currentState === WorkflowState.PendingForDOAGApproval.value &&
			(authenticatedUser.userId === this.approvingUser?.id || authenticatedUser.userId === this.delegatedApprovingUser?.id)
		);
	}

	canHaveApplicationApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			currentState === WorkflowState.PendingApplicationApproval.value &&
			(authenticatedUser.userEmail === this.appEndorser?.customFields.contact_email)
		);
	}


	canHaveTechnicalDecision(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			(authenticatedUser.isInRole(UserRole.AWSPORTechnicalReviewer)
			|| authenticatedUser.isInRole(UserRole.AWSPORSolArqReviewer)
			|| authenticatedUser.isInRole(UserRole.AWSPORCyberReviewer)
			|| authenticatedUser.isInRole(UserRole.AWSPORTechnicalImplementer))
			&&
			[
				WorkflowState.PendingNetworkPolicyApproval.value,
				WorkflowState.PendingSolutionArchitectureApproval.value,
				WorkflowState.PendingForCyberApproval.value,
			].includes(currentState)
		);
	}

	canHaveEscalateDecision(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			(authenticatedUser.isInRole(UserRole.AWSPORTechnicalReviewer) && currentState === WorkflowState.PendingNetworkPolicyApproval.value)
			|| (authenticatedUser.isInRole(UserRole.AWSPORSolArqReviewer) && currentState === WorkflowState.PendingSolutionArchitectureApproval.value)
		);
	}

	canSendToReview(accountOwnerId: string, workflowInstance: WorkflowInstance): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return(
			(
				(currentState === WorkflowState.PendingNetworkPolicyApproval.value && authenticatedUser.isInRole(UserRole.AWSPORTechnicalReviewer)) ||
				(currentState === WorkflowState.PendingSolutionArchitectureApproval.value && authenticatedUser.isInRole(UserRole.AWSPORSolArqReviewer)) ||
				(currentState === WorkflowState.PendingForCyberApproval.value && authenticatedUser.isInRole(UserRole.AWSPORCyberReviewer)) ||
				(currentState === WorkflowState.PendingApplicationApproval.value && authenticatedUser.userId === this.appEndorser?.id) ||
				(currentState === WorkflowState.PendingForDOAGApproval.value && authenticatedUser.userId === this.approvingUser?.id) ||
				//the account owner can sen to review only in his step
				(currentState === WorkflowState.PendingAccountOwnerApproval.value && accountOwnerId === authenticatedUser.userId) ||
				//the Requestor can send to review his own POR
				((!this.isInmutableState() && !(currentState === WorkflowState.AwaitingImplementation.value)) && workflowInstance.createdBy.id === authenticatedUser.userId )
			) &&
			//The request cannot be in Review or Draft
			!this.isInEditableState()
		)
	}

	isInEditableState() {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return currentState == WorkflowState.Review.value || currentState == WorkflowState.Draft.value;
	}


	isInmutableState() {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return currentState == WorkflowState.Completed.value  || currentState == WorkflowState.Rejected.value || 
			   currentState == WorkflowState.Cancelled.value || currentState == WorkflowState.ImplementationFailed.value;
	}

	canHaveAccountOwnerDecision(accountOwnerId: string, secondaryAccountOwnerId : string, hasCustodianApproval: boolean): boolean {
       const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState;
       if (hasCustodianApproval){
           return currentState === WorkflowState.PendingAccountOwnerApproval && (
               accountOwnerId === authenticatedUser.userId || secondaryAccountOwnerId == authenticatedUser.userId);
       }
       return (accountOwnerId === authenticatedUser.userId && currentState === WorkflowState.PendingAccountOwnerApproval) ||
	          (secondaryAccountOwnerId == authenticatedUser.userId && currentState === WorkflowState.PendingSecondaryAccountOwnerApproval) ||
			  (this.direction.value == PortOpeningRequestsDirection.ExternalAccounts.value && authenticatedUser.isInRole(`AWS_${this.accountName}_Approver`));
   }

	isEditable(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			authenticatedUser.userId === this.workflowInstance.createdBy.id &&
			[
				WorkflowState.Draft.value,
				WorkflowState.Review.value,
			].includes(currentState)
		);
	}

	architectApprovalBypassed(): boolean {
		const workflowInstanceStates = this.workflowInstance.workflowInstanceStates;
		return (workflowInstanceStates.find((workflowInstanceState) => workflowInstanceState.workflowState.value == WorkflowState.PendingSolutionArchitectureApproval.value) == undefined) && !this.escalated;
	}

	cyberApprovalBypassed(): boolean {
		const workflowInstanceStates = this.workflowInstance.workflowInstanceStates;
		return (workflowInstanceStates.find((workflowInstanceState) => workflowInstanceState.workflowState.value == WorkflowState.PendingForCyberApproval.value) == undefined) && !this.escalated;
	}

	isGLLAwsToGLLAwsScope () :boolean {
		console.log(this.type?.name == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name)
		return this.type?.name == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name;
	}

}